import { useRef, useState } from 'preact/hooks';
import { todayISO } from '../../lib/dates.ts';
import { SUBJECTS } from '../../lib/subjects.ts';
import { live, useStore } from '../../store/store.ts';
import { studentDisplayName } from '../../lib/student-roster.ts';
import { closeModal, toast } from '../../ui/ui-store.ts';
import { DrawCanvas, type DrawCanvasHandle } from './DrawCanvas.tsx';

/**
 * One observation, new or being corrected.
 *
 * With `observationId` the form edits an existing note instead of adding one —
 * that is the only way to fix a quick note, which files itself on a pupil,
 * a lesson and a subject all guessed from a single typed line.
 */
export function ObservationModal({
  studentId,
  observationId,
}: {
  studentId?: string;
  observationId?: string;
}) {
  const students = live(useStore((s) => s.students));
  const selected = useStore((s) => s.selectedStudentId);
  const existing = live(useStore((s) => s.observations)).find((o) => o.id === observationId) ?? null;

  const [student, setStudent] = useState(existing?.studentId ?? studentId ?? selected ?? '');
  const [date, setDate] = useState(existing?.date || todayISO());
  const [subject, setSubject] = useState(existing?.subject || (SUBJECTS[0][0] as string));
  const [difficulty, setDifficulty] = useState(existing?.difficulty ?? '');
  const [note, setNote] = useState(existing?.note ?? '');
  const [support, setSupport] = useState(existing?.support ?? '');

  const canvas = useRef<DrawCanvasHandle | null>(null);

  function save() {
    // Editing never touches the stylus PNG: it is stored under the
    // observation id, outside the store, and there is nothing to redraw here.
    const handwriting = existing ? null : (canvas.current?.toDataUrl() ?? null);

    if (!student || (!note.trim() && !handwriting && !existing?.hasHandwriting)) {
      toast('Choisis un élève et écris une observation');
      return;
    }

    const input = {
      studentId: student,
      // Only the quick note reads the clock. Here the date is picked by
      // hand and may be any day of the year, so there is no lesson this
      // form could infer without guessing — but a note that already knows
      // its lesson keeps it.
      lessonId: existing?.lessonId ?? '',
      date,
      subject,
      difficulty: difficulty.trim(),
      note,
      support: support.trim(),
    };

    if (existing) {
      useStore.getState().updateObservation(existing.id, input);
      closeModal();
      toast('Observation modifiée');
      return;
    }

    useStore.getState().addObservation(input, handwriting);
    closeModal();
    toast('Observation rangée dans le dossier');
  }

  return (
    <>
      <div class="formGrid">
        <div>
          <label for="ostudent">Élève</label>
          <select id="ostudent" value={student} onChange={(e) => setStudent(e.currentTarget.value)}>
            <option value="">Choisir un élève</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>
                {studentDisplayName(s.name)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label for="odate">Date</label>
          <input id="odate" type="date" value={date} onInput={(e) => setDate(e.currentTarget.value)} />
        </div>

        <div>
          <label for="osub">Matière</label>
          <select id="osub" value={subject} onChange={(e) => setSubject(e.currentTarget.value)}>
            {SUBJECTS.map(([name]) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label for="odiff">Difficulté / point d’attention</label>
          <input
            id="odiff"
            value={difficulty}
            placeholder="Ex. ne lit pas la consigne"
            lang="fr"
            spellcheck={true}
            autocorrect="on"
            autocapitalize="sentences"
            aria-describedby="observation-spelling-hint"
            onInput={(e) => setDifficulty(e.currentTarget.value)}
          />
        </div>

        <div style={{ gridColumn: '1/-1' }}>
          <label for="onote">Observation tapée</label>
          <textarea
            id="onote"
            value={note}
            placeholder="Tu peux taper ici ou écrire au stylet juste en dessous."
            lang="fr"
            spellcheck={true}
            autocorrect="on"
            autocapitalize="sentences"
            aria-describedby="observation-spelling-hint"
            onInput={(e) => setNote(e.currentTarget.value)}
          />
        </div>

        <div style={{ gridColumn: '1/-1' }}>
          <label for="osupport">Matériel / aide / remédiation</label>
          <textarea
            id="osupport"
            value={support}
            placeholder="Ex. matériel concret, reformulation, groupe de besoin…"
            lang="fr"
            spellcheck={true}
            autocorrect="on"
            autocapitalize="sentences"
            aria-describedby="observation-spelling-hint"
            onInput={(e) => setSupport(e.currentTarget.value)}
          />
        </div>

        <p id="observation-spelling-hint" class="spellingHint" style={{ gridColumn: '1/-1' }}>
          Les mots mal orthographiés sont soulignés. Appuie sur un mot (ou fais un clic droit)
          pour afficher les corrections proposées.
        </p>
      </div>

      {existing ? (
        existing.hasHandwriting && (
          <p class="small muted" style={{ marginTop: '14px' }}>
            La note manuscrite au stylet est conservée telle quelle.
          </p>
        )
      ) : (
        <div class="detailSection">
          <div class="row">
            <div>
              <label style={{ margin: 0 }}>Note manuscrite au stylet</label>
              <div class="small muted">Écris directement dans la zone blanche.</div>
            </div>
            <span class="spacer" />
            <button class="btn" onClick={() => canvas.current?.clear()}>
              Effacer
            </button>
          </div>
          <DrawCanvas handleRef={canvas} />
        </div>
      )}

      <div style={{ marginTop: '15px' }}>
        <button class="btn primary" onClick={save}>
          {existing ? 'Enregistrer les modifications' : 'Enregistrer dans le dossier de l’élève'}
        </button>
      </div>
    </>
  );
}
